// lineageView.js — pure view helpers for the Lineage X-ray (label shaping +
// the bipartite flow-graph layout). Extracted from XRayBody so the non-trivial
// layout math is unit-testable in isolation, like the sibling pure geometry
// libs (flowGeometry.js, compositionLayout.js). No React, no DOM.

// Longest shared '/'-segment prefix across a set of paths — the worktree root
// everything hangs off — so labels can drop it and show the part that differs.
// Callers must pass FILE paths only: a URL ("https://x.com/doc") shares no
// leading segment with a filesystem path, so mixing them collapses the prefix
// to "" and defeats stripping for every file in the window.
export function commonPathPrefix(refs) {
  const paths = refs.filter((r) => typeof r === "string" && r.includes("/"));
  if (paths.length < 2) return "";
  let segs = paths[0].split("/");
  for (const p of paths.slice(1)) {
    const s = p.split("/");
    let i = 0;
    while (i < segs.length && i < s.length && segs[i] === s[i]) i++;
    segs = segs.slice(0, i);
    if (!segs.length) break;
  }
  return segs.join("/");
}

export function relOf(ref, prefix) {
  if (typeof ref !== "string") return "";
  return prefix && ref.startsWith(`${prefix}/`) ? ref.slice(prefix.length + 1) : ref;
}

// Short one-line label for a node in the flow graph (SVG text can't wrap or
// ellipsis, so this returns an already-short string; the caller truncates).
export function nodeShortLabel(node, prefix) {
  if (node.kind && node.kind.startsWith("file") && node.ref) {
    const rel = relOf(node.ref, prefix);
    return rel.slice(rel.lastIndexOf("/") + 1) || rel;
  }
  if (node.kind === "web.fetch" && node.ref) return node.ref.replace(/^https?:\/\//, "").split("/")[0];
  if (node.kind === "web.search") return node.intent || node.label || "";
  return node.ref || node.label || "";
}

// Deterministic bipartite layout, deduped by ARTIFACT (a file read 3× is one
// node, not three): sources left, produced targets right. Rows ordered to cut
// crossings (targets by degree; sources by the barycentre of the targets they
// feed) and each column vertically CENTERED so the two sides align on a shared
// midline and the whole band centers in the panel height H.
//   → { leftPos, rightPos, edges, svgH, W, leftX, rightX, LABEL_W, dropped }
export function computeFlowLayout(built, W, H) {
  const LABEL_W = Math.min(230, Math.round(W * 0.26));
  const leftX = LABEL_W;
  const rightX = W - LABEL_W;
  const rowH = 24;
  const topPad = 16;
  const MAX_SIDE = 60;
  const fam = (k) => (k.startsWith("file") ? "file" : k.startsWith("linear") ? "linear" : "web");
  const keyOf = (n) => `${fam(n.kind)}:${n.ref || n.label}`;

  const srcNode = new Map();
  const tgtNode = new Map();
  // sk -> (tk -> strongest edge): a nested Map, not a joined-string key. A prior
  // string join used a raw NUL delimiter, which made this small file read as
  // binary to git/Forgejo (breaking diff/blame); a nested Map needs no delimiter.
  const pair = new Map();
  for (const e of built.edges) {
    const sk = keyOf(e.source);
    const tk = keyOf(e.target);
    if (!srcNode.has(sk)) srcNode.set(sk, e.source);
    if (!tgtNode.has(tk)) tgtNode.set(tk, e.target);
    let inner = pair.get(sk);
    if (!inner) { inner = new Map(); pair.set(sk, inner); }
    const cur = inner.get(tk);
    if (!cur || e.score > cur.score) inner.set(tk, { sk, tk, class: e.class, tier: e.tier, score: e.score });
  }
  const pairs = [];
  for (const inner of pair.values()) for (const e of inner.values()) pairs.push(e);
  const srcDeg = new Map();
  const tgtDeg = new Map();
  for (const { sk, tk } of pairs) {
    srcDeg.set(sk, (srcDeg.get(sk) || 0) + 1);
    tgtDeg.set(tk, (tgtDeg.get(tk) || 0) + 1);
  }
  const rightKeys = [...tgtNode.keys()].sort((a, b) => (tgtDeg.get(b) - tgtDeg.get(a)) || a.localeCompare(b)).slice(0, MAX_SIDE);
  const rightIdx = new Map(rightKeys.map((k, i) => [k, i]));

  const srcRows = new Map();
  for (const { sk, tk } of pairs) {
    if (!rightIdx.has(tk)) continue;
    const arr = srcRows.get(sk) || [];
    arr.push(rightIdx.get(tk));
    srcRows.set(sk, arr);
  }
  let leftKeys = [...srcNode.keys()].filter((k) => srcRows.has(k));
  const bary = new Map(leftKeys.map((k) => { const r = srcRows.get(k); return [k, r.reduce((a, b) => a + b, 0) / r.length]; }));
  leftKeys.sort((a, b) => (bary.get(a) - bary.get(b)) || (srcDeg.get(b) - srcDeg.get(a)));
  leftKeys = leftKeys.slice(0, MAX_SIDE);
  const leftIdx = new Map(leftKeys.map((k, i) => [k, i]));

  const rows = Math.max(leftKeys.length, rightKeys.length, 1);
  const bandH = rows * rowH;
  const svgH = Math.max(topPad * 2 + bandH, H || 0);
  const bandTop = Math.max(topPad, (svgH - bandH) / 2);
  const leftOff = (bandH - leftKeys.length * rowH) / 2;
  const rightOff = (bandH - rightKeys.length * rowH) / 2;
  const yL = (i) => bandTop + leftOff + i * rowH + rowH / 2;
  const yR = (i) => bandTop + rightOff + i * rowH + rowH / 2;

  const leftPos = new Map(leftKeys.map((k) => [k, { x: leftX, y: yL(leftIdx.get(k)), node: srcNode.get(k) }]));
  const rightPos = new Map(rightKeys.map((k) => [k, { x: rightX, y: yR(rightIdx.get(k)), node: tgtNode.get(k) }]));
  const edges = pairs.filter((e) => leftPos.has(e.sk) && rightPos.has(e.tk)).sort((a, b) => b.score - a.score).slice(0, 600);
  const dropped = (srcNode.size - leftKeys.length) + (tgtNode.size - rightKeys.length);
  return { leftPos, rightPos, edges, svgH, W, leftX, rightX, LABEL_W, dropped };
}
